import { useEffect, useState, type ComponentType, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Gauge, Receipt, ShieldCheck, Sparkles, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiErrorMessage } from "@/api/perkpoint/client";
import { OutletsApi, outletKeys } from "@/api/perkpoint/outlets";
import { cn } from "@/lib/utils";
import type { ApiFraudRule, FraudRuleInput } from "@/api/perkpoint/fraud";
import {
  ACTION_OPTIONS,
  SEVERITY_DOT,
  SEVERITY_LABELS,
  describeRule,
  type RuleAction,
  type Severity,
} from "./fraud-data";

interface OutletOption {
  id: string;
  name: string;
}

// The backend matches rules to transactions by outlet name (ledger_entries.outlet), so the
// select's values are names, not the legacy outlet ids.
const ALL_OUTLETS = "all";
const actionEnum = z.enum(["flag", "block-earn-burn", "block-all-future"]);
const isPositiveInt = (value: string) => /^\d+$/.test(value) && Number(value) > 0;
const isPositiveAmount = (value: string) => /^\d+(\.\d{1,4})?$/.test(value) && Number(value) > 0;

const schema = z
  .object({
    name: z.string().trim().min(1, "Give the rule a name"),
    outletId: z.string().min(1),
    severity: z.enum(["low", "medium", "high", "critical"]),
    enabled: z.boolean(),
    velocityOn: z.boolean(),
    velocityTransactions: z.string(),
    velocityDays: z.string(),
    velocityAction: actionEnum,
    invoiceOn: z.boolean(),
    invoiceAmount: z.string(),
    invoiceAction: actionEnum,
    redemptionOn: z.boolean(),
    redemptionPoints: z.string(),
    redemptionAction: actionEnum,
  })
  .superRefine((values, ctx) => {
    if (!values.velocityOn && !values.invoiceOn && !values.redemptionOn) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["conditions"],
        message: "Turn on at least one condition",
      });
    }
    const requirePositive = (on: boolean, field: keyof typeof values, value: string) => {
      if (on && !isPositiveInt(value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: "Enter a whole number above 0" });
      }
    };
    requirePositive(values.velocityOn, "velocityTransactions", values.velocityTransactions);
    requirePositive(values.velocityOn, "velocityDays", values.velocityDays);
    requirePositive(values.redemptionOn, "redemptionPoints", values.redemptionPoints);
    if (values.invoiceOn && !isPositiveAmount(values.invoiceAmount)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["invoiceAmount"], message: "Enter an amount above 0" });
    }
    if (values.velocityOn && Number(values.velocityDays) > 365) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["velocityDays"], message: "At most 365 days" });
    }
  });

type FormValues = z.infer<typeof schema>;

function toFormValues(rule?: ApiFraudRule): FormValues {
  return {
    name: rule?.name ?? "",
    outletId: rule?.outlets[0] ?? ALL_OUTLETS,
    severity: rule?.severity ?? "high",
    enabled: rule?.enabled ?? true,
    velocityOn: rule ? !!rule.velocity : true,
    velocityTransactions: String(rule?.velocity?.transactions ?? 10),
    velocityDays: String(rule?.velocity?.days ?? 1),
    velocityAction: rule?.velocity?.action ?? "flag",
    invoiceOn: !!rule?.invoice,
    invoiceAmount: String(rule?.invoice?.amount ?? 25000),
    invoiceAction: rule?.invoice?.action ?? "flag",
    redemptionOn: !!rule?.redemption,
    redemptionPoints: String(rule?.redemption?.points ?? 5000),
    redemptionAction: rule?.redemption?.action ?? "block-earn-burn",
  };
}

/** Form values → the API body. A condition that is off is sent as null, which removes it on edit. */
function toInput(values: FormValues): FraudRuleInput {
  return {
    name: values.name.trim(),
    outlets: values.outletId === ALL_OUTLETS ? [] : [values.outletId],
    severity: values.severity,
    status: values.enabled ? "active" : "paused",
    velocity: values.velocityOn
      ? {
          transactions: Number(values.velocityTransactions),
          days: Number(values.velocityDays),
          action: values.velocityAction,
        }
      : null,
    invoice: values.invoiceOn ? { amount: Number(values.invoiceAmount), action: values.invoiceAction } : null,
    redemption: values.redemptionOn
      ? { points: Number(values.redemptionPoints), action: values.redemptionAction }
      : null,
  };
}

const PRESETS: { label: string; values: Partial<FormValues> }[] = [
  {
    label: "Rapid redemptions",
    values: {
      name: "Rapid redemption guard",
      severity: "high",
      velocityOn: true,
      velocityTransactions: "10",
      velocityDays: "1",
      velocityAction: "block-earn-burn",
      invoiceOn: false,
      redemptionOn: false,
    },
  },
  {
    label: "High-value bills",
    values: {
      name: "High-value bill check",
      severity: "critical",
      velocityOn: false,
      invoiceOn: true,
      invoiceAmount: "50000",
      invoiceAction: "flag",
      redemptionOn: true,
      redemptionPoints: "10000",
      redemptionAction: "block-all-future",
    },
  },
  {
    label: "Points dumping",
    values: {
      name: "Bulk points burn",
      severity: "medium",
      velocityOn: false,
      invoiceOn: false,
      redemptionOn: true,
      redemptionPoints: "5000",
      redemptionAction: "flag",
    },
  },
];

function ConditionCard({
  icon: Icon,
  title,
  description,
  on,
  onToggle,
  children,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
  on: boolean;
  onToggle: (on: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-lg border transition-colors",
        on ? "border-green-300 bg-green-50/40" : "border-slate-200 bg-white",
      )}
    >
      <label className="flex cursor-pointer items-start gap-3 p-4">
        <div
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-md",
            on ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500",
          )}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900">{title}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <Switch checked={on} onCheckedChange={onToggle} aria-label={title} />
      </label>
      {on && <div className="space-y-3 border-t border-green-200/70 px-4 pb-4 pt-3">{children}</div>}
    </div>
  );
}

function ActionSelect({ value, onChange }: { value: RuleAction; onChange: (value: RuleAction) => void }) {
  return (
    <Select value={value} onValueChange={(next) => onChange(next as RuleAction)}>
      <SelectTrigger className="bg-white">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {ACTION_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <div>
              <p>{option.label}</p>
              <p className="text-xs text-muted-foreground">{option.hint}</p>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface FraudRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rule being edited; omit to create a new one. */
  rule?: ApiFraudRule;
  /** The program's currency code, for the invoice threshold. */
  currency: string;
  /** Program whose outlets fill the dropdown. */
  programId: string | null;
  /** Saves through the API. Rejects to keep the dialog open (the caller shows the error). */
  onSave: (input: FraudRuleInput) => Promise<void>;
}

export function FraudRuleDialog({ open, onOpenChange, rule, currency, programId, onSave }: FraudRuleDialogProps) {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const outletsQuery = useQuery({
    queryKey: outletKeys.list(programId ?? "", { page_size: 100 }),
    queryFn: () => OutletsApi.list(programId!, { page: 1, page_size: 100 }),
    enabled: open && Boolean(programId),
  });
  const outlets: OutletOption[] = (outletsQuery.data?.items ?? []).map((outlet) => ({
    id: outlet.id,
    name: outlet.name,
  }));
  const outletsLoading = outletsQuery.isLoading;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toFormValues(rule),
  });
  const values = useWatch({ control: form.control }) as FormValues;
  const conditionsError = (form.formState.errors as Record<string, { message?: string } | undefined>)
    .conditions?.message;

  useEffect(() => {
    if (open) form.reset(toFormValues(rule));
  }, [open, rule, form]);

  useEffect(() => {
    if (!open || !outletsQuery.isError) return;
    toast({
      title: "Error",
      description: apiErrorMessage(outletsQuery.error, "Failed to fetch outlets"),
      variant: "destructive",
    });
  }, [open, outletsQuery.isError, outletsQuery.error, toast]);

  const applyPreset = (preset: Partial<FormValues>) => {
    form.reset({ ...form.getValues(), ...preset });
  };

  const setCondition = (field: "velocityOn" | "invoiceOn" | "redemptionOn", on: boolean) => {
    form.setValue(field, on, { shouldDirty: true });
    if (form.formState.isSubmitted) void form.trigger();
  };

  const onSubmit = async (data: FormValues) => {
    setSaving(true);
    try {
      await onSave(toInput(data));
      onOpenChange(false);
    } catch {
      // The caller has shown the error; keep the form so nothing typed is lost.
    } finally {
      setSaving(false);
    }
  };

  // The saved outlet may be missing from the legacy outlet list; keep it selectable.
  const outletOptions =
    rule?.outlets[0] && !outlets.some((outlet) => outlet.name === rule.outlets[0])
      ? [{ id: rule.outlets[0], name: rule.outlets[0] }, ...outlets]
      : outlets;

  const preview = (() => {
    const safe = (value: string) => (isPositiveAmount(value ?? "") ? Number(value) : 0);
    return describeRule({
      velocity: values.velocityOn
        ? {
            transactions: safe(values.velocityTransactions),
            days: safe(values.velocityDays),
            action: values.velocityAction,
          }
        : undefined,
      invoice: values.invoiceOn ? { amount: safe(values.invoiceAmount), action: values.invoiceAction } : undefined,
      redemption: values.redemptionOn
        ? { points: safe(values.redemptionPoints), action: values.redemptionAction }
        : undefined,
    }, currency);
  })();

  const scopeLabel = values.outletId === ALL_OUTLETS ? "all outlets" : values.outletId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>{rule ? "Edit fraud rule" : "New fraud rule"}</DialogTitle>
          <DialogDescription>
            Choose what counts as suspicious and what happens when a transaction matches.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {!rule && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                  <Sparkles className="h-3.5 w-3.5" /> Start from
                </span>
                {PRESETS.map((preset) => (
                  <Button
                    key={preset.label}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 rounded-full bg-white text-xs"
                    onClick={() => applyPreset(preset.values)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            )}

            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
              <div className="min-w-0 space-y-6">
                <section className="space-y-4">
                  <h3 className="text-sm font-semibold text-slate-900">1. Basics</h3>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Rule name</FormLabel>
                          <FormControl>
                            <Input placeholder="e.g. Rapid redemption guard" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="outletId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Applies to</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value={ALL_OUTLETS}>All outlets</SelectItem>
                              {outletsLoading && (
                                <div className="px-2 py-1.5 text-xs text-muted-foreground">Loading outlets…</div>
                              )}
                              {outletOptions.map((outlet) => (
                                <SelectItem key={outlet.id} value={outlet.name}>
                                  {outlet.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="severity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Alert severity</FormLabel>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup">
                          {(Object.keys(SEVERITY_LABELS) as Severity[]).map((severity) => (
                            <button
                              key={severity}
                              type="button"
                              role="radio"
                              aria-checked={field.value === severity}
                              onClick={() => field.onChange(severity)}
                              className={cn(
                                "flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors",
                                field.value === severity
                                  ? "border-slate-900 bg-slate-900 text-white"
                                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                              )}
                            >
                              <span className={cn("h-2 w-2 rounded-full", SEVERITY_DOT[severity])} />
                              {SEVERITY_LABELS[severity]}
                            </button>
                          ))}
                        </div>
                      </FormItem>
                    )}
                  />
                </section>

                <section className="space-y-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">2. Conditions</h3>
                    <p className="text-xs text-muted-foreground">
                      Each condition you turn on is checked separately and has its own action.
                    </p>
                  </div>

                  <ConditionCard
                    icon={Zap}
                    title="Transaction velocity"
                    description="Too many transactions from one member in a short window"
                    on={values.velocityOn}
                    onToggle={(on) => setCondition("velocityOn", on)}
                  >
                    <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-2">
                      <FormField
                        control={form.control}
                        name="velocityTransactions"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Transactions</FormLabel>
                            <FormControl>
                              <Input type="number" min={1} className="bg-white" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <span className="pt-8 text-sm text-muted-foreground">within</span>
                      <FormField
                        control={form.control}
                        name="velocityDays"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Days</FormLabel>
                            <FormControl>
                              <Input type="number" min={1} className="bg-white" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="velocityAction"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-xs">Then</FormLabel>
                          <ActionSelect value={field.value} onChange={field.onChange} />
                        </FormItem>
                      )}
                    />
                  </ConditionCard>

                  <ConditionCard
                    icon={Receipt}
                    title="Invoice amount"
                    description="A single bill far above normal spend"
                    on={values.invoiceOn}
                    onToggle={(on) => setCondition("invoiceOn", on)}
                  >
                    <div className="grid gap-3 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="invoiceAmount"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Invoice over ({currency})</FormLabel>
                            <FormControl>
                              <Input type="number" min={0} step="any" className="bg-white" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="invoiceAction"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Then</FormLabel>
                            <ActionSelect value={field.value} onChange={field.onChange} />
                          </FormItem>
                        )}
                      />
                    </div>
                  </ConditionCard>

                  <ConditionCard
                    icon={Gauge}
                    title="Points redemption"
                    description="Too many points burned on one bill"
                    on={values.redemptionOn}
                    onToggle={(on) => setCondition("redemptionOn", on)}
                  >
                    <div className="grid gap-3 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="redemptionPoints"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Points over</FormLabel>
                            <FormControl>
                              <Input type="number" min={1} className="bg-white" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="redemptionAction"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Then</FormLabel>
                            <ActionSelect value={field.value} onChange={field.onChange} />
                          </FormItem>
                        )}
                      />
                    </div>
                  </ConditionCard>

                  {conditionsError && (
                    <p className="text-sm font-medium text-destructive">{conditionsError}</p>
                  )}
                </section>
              </div>

              <aside className="h-fit space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4 lg:sticky lg:top-0">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <ShieldCheck className="h-4 w-4 text-green-700" />
                  Rule summary
                </div>
                <div className="space-y-1 text-sm">
                  <p className="font-medium text-slate-900">{values.name?.trim() || "Untitled rule"}</p>
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className={cn("h-2 w-2 rounded-full", SEVERITY_DOT[values.severity])} />
                    {SEVERITY_LABELS[values.severity]} severity · {scopeLabel}
                  </p>
                </div>
                {preview.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No conditions on yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {preview.map((line) => (
                      <li
                        key={line}
                        className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs leading-relaxed text-slate-700"
                      >
                        {line}
                      </li>
                    ))}
                  </ul>
                )}
                <FormField
                  control={form.control}
                  name="enabled"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between gap-3 space-y-0 border-t border-slate-200 pt-4">
                      <div>
                        <FormLabel className="text-sm">Turn on now</FormLabel>
                        <p className="text-xs text-muted-foreground">Start checking transactions on save</p>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </aside>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" className="bg-green-600 text-white hover:bg-green-700" disabled={saving}>
                {saving ? "Saving…" : rule ? "Save changes" : "Create rule"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
